"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import Link from "@/components/ui/intent-link";
import { useState } from "react";

import {
  CONSENT_CATEGORIES,
  useConsent,
  type Consent,
  type ConsentCategory,
} from "@/lib/consent-context";

export function ConsentModal() {
  const { consent, isSettingsOpen, closeSettings, acceptAll, rejectAll, savePartial } =
    useConsent();

  const [draft, setDraft] = useState<Consent>(consent);
  const [wasOpen, setWasOpen] = useState(isSettingsOpen);
  if (wasOpen !== isSettingsOpen) {
    setWasOpen(isSettingsOpen);
    if (isSettingsOpen) setDraft(consent);
  }

  const toggle = (key: ConsentCategory) => {
    setDraft((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <DialogPrimitive.Root
      open={isSettingsOpen}
      onOpenChange={(open) => (open ? null : closeSettings())}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-80 bg-black/60 md:backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed top-1/2 left-1/2 z-80 flex max-h-[min(85dvh,720px)] w-[min(640px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-[20px] bg-white text-black shadow-[0_30px_80px_-20px_rgba(13,10,44,0.45)] outline-none"
        >
          <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-3 md:px-8 md:pt-7">
            <div className="grid gap-1.5">
              <span className="text-teal-07 text-xs font-bold tracking-wider uppercase">
                Cookie Centre
              </span>
              <DialogPrimitive.Title className="text-teal-08 md:text-h6 text-lg leading-tight font-bold tracking-tight">
                Manage cookies
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-sm leading-relaxed text-black/65">
                Choose which categories of cookies we can use on this site. You can change your mind
                any time via the Cookie Policy.
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              aria-label="Close"
              className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-black/5 text-black/55 transition-colors hover:bg-black/10 hover:text-black"
            >
              <X className="size-4" strokeWidth={2.25} aria-hidden />
            </DialogPrimitive.Close>
          </div>

          <div className="min-h-0 flex-1 scrollbar-none overflow-y-auto overscroll-contain px-6 md:px-8">
            <ul className="flex flex-col divide-y divide-black/10 border-y border-black/10">
              {CONSENT_CATEGORIES.map((cat) => {
                const isOn = draft[cat.key];
                const isLocked = cat.required;
                return (
                  <li key={cat.key} className="flex items-start gap-4 py-4 md:py-5">
                    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                      <span className="text-teal-08 text-base leading-tight font-bold md:text-lg">
                        {cat.label}
                        {isLocked && (
                          <span className="text-teal-07 ml-2 align-middle text-xs font-bold tracking-wider uppercase">
                            Always on
                          </span>
                        )}
                      </span>
                      <p className="text-sm leading-relaxed text-black/65">{cat.description}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isOn}
                      aria-label={`${cat.label} cookies`}
                      disabled={isLocked}
                      onClick={() => toggle(cat.key)}
                      className={`relative mt-1 h-7 w-12 shrink-0 rounded-full transition-colors ${
                        isOn ? (isLocked ? "bg-teal-05" : "bg-teal-07") : "bg-black/15"
                      } ${isLocked ? "cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <span
                        aria-hidden
                        className={`absolute top-1 left-1 size-5 rounded-full bg-white shadow-[0_2px_4px_rgba(0,0,0,0.18)] transition-transform ${
                          isOn ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>

            <p className="mt-5 pb-6 text-sm leading-relaxed text-black/55 md:pb-8">
              To see the full list of cookies that are being tracked on this site, please visit the{" "}
              <Link
                href="/cookie-declaration"
                onClick={closeSettings}
                className="text-teal-07 underline-offset-2 hover:underline"
              >
                Cookie Declaration page
              </Link>
              .
            </p>
          </div>

          <div className="flex flex-col gap-2 border-t border-black/10 p-4 pb-5 md:flex-row md:items-center md:justify-end md:gap-3 md:px-8 md:py-5">
            <button
              type="button"
              onClick={rejectAll}
              className="text-navy-03 hover:bg-teal-01 inline-flex h-11 cursor-pointer items-center justify-center rounded-lg border border-black/15 px-5 text-sm font-bold transition-colors md:order-1"
            >
              Reject all
            </button>
            <button
              type="button"
              onClick={() => savePartial(draft)}
              className="border-teal-07 text-teal-07 hover:bg-teal-01 inline-flex h-11 cursor-pointer items-center justify-center rounded-lg border px-5 text-sm font-bold transition-colors md:order-2"
            >
              Save preferences
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="bg-teal-07 hover:bg-teal-08 inline-flex h-11 cursor-pointer items-center justify-center rounded-lg px-5 text-sm font-bold text-white transition-colors md:order-3"
            >
              Allow all
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
